import { type AnalyticsEvent } from '@autone/analytics';
import { useMemo, useState } from 'react';
import { type Control, type FieldValues, type Path } from 'react-hook-form';

import { type GetCoreLocationClustersRequest } from '../../../redux/services';
import { useGetCoreLocationClustersQuery } from '../../../redux/services/core';
import { TailwindFormField } from '../../../tailwind';
import { MultiSelectDropdown } from '../../multi-select-dropdown/MultiSelectDropdown';
import { useMultiselectApiFilters } from '../hooks';
import { type GetMultiselectMapper } from '../types';
import { CLIENT_SIDE_SEARCH_DROPDOWN_LIMIT } from '../utilities';

type LocationClustersFilters =
  GetCoreLocationClustersRequest['body']['filters'];

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  locationsMap?: GetMultiselectMapper<LocationClustersFilters['geographic'], T>;
  productMap?: GetMultiselectMapper<LocationClustersFilters['product'], T>;
  catalogueMap?: GetMultiselectMapper<LocationClustersFilters['catalogue'], T>;
  productGroupsMap?: GetMultiselectMapper<
    LocationClustersFilters['product_groups'],
    T
  >;
  disabled?: boolean;
  analyticsEvent?: AnalyticsEvent;
}

export const CoreLocationClustersMultiselect = <T extends FieldValues>({
  control,
  name,
  locationsMap,
  productMap,
  catalogueMap,
  productGroupsMap,
  disabled,
  analyticsEvent,
}: Props<T>) => {
  const geographicFilters = useMultiselectApiFilters({
    control,
    map: locationsMap,
    relevantFilterKeys: ['regions', 'locations', 'countries', 'territories'],
  });

  const productFilters = useMultiselectApiFilters({
    control,
    map: productMap,
    relevantFilterKeys: [
      'departments',
      'sub_departments',
      'brands',
      'seasons',
      'events',
      'collection_types',
    ],
  });

  const catalogueFilters = useMultiselectApiFilters({
    control,
    map: catalogueMap,
    relevantFilterKeys: ['seasons', 'events'],
  });

  const productGroupsFilters = useMultiselectApiFilters({
    control,
    map: productGroupsMap,
    relevantFilterKeys: ['groups'],
  });

  const [searchTerm, setSearchTerm] = useState('');
  const { data, isLoading } = useGetCoreLocationClustersQuery({
    query: { limit: CLIENT_SIDE_SEARCH_DROPDOWN_LIMIT },
    body: {
      filters: {
        geographic: geographicFilters,
        location_cluster: {},
        location_groups: {},
        product: productFilters,
        catalogue: catalogueFilters,
        product_groups: productGroupsFilters,
        size_run: {},
        size: {},
      },
    },
  });

  const options = useMemo(
    () =>
      data?.items
        .map(({ cluster_name }) => cluster_name)
        .filter((itemName) =>
          itemName.toLowerCase().includes(searchTerm.toLowerCase()),
        ) ?? [],
    [data, searchTerm],
  );

  return (
    <TailwindFormField
      control={control}
      name={name}
      render={({ field: { value, onChange, disabled: fieldDisabled } }) => (
        <MultiSelectDropdown
          className="h-fit"
          loading={isLoading}
          options={options}
          getOptionId={(option) => option}
          getOptionLabel={(option) => option}
          onSearchTermChange={setSearchTerm}
          selected={value ?? []}
          onSelectedChange={(ids) => onChange(ids)}
          disabled={disabled || fieldDisabled}
          analyticsEvent={analyticsEvent}
        />
      )}
    />
  );
};
